"use client";

import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Briefcase, Clock, FileText, Users, MessageCircle, TrendingUp, CheckCircle, Calendar, BookOpen, Zap, BarChart3, Users2, Mail, FileText as FileTextIcon, Clock as ClockIcon, Building, Menu, Palette, Check } from 'lucide-react'; // ONLY lucide-react - no Heroicons
import { Badge } from '@/components/ui/badge';

const Home = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">Loading...</div>
    );
  }

  if (user) return null; // Redirect handled above

  // Enhanced feature cards with value-focused descriptions
  const features = [
    {
      icon: Briefcase,
      title: "Master Project & Task Management",
      description: "Streamline your entire workflow with intuitive tools that eliminate chaos. Assign tasks effortlessly, track progress in real-time, and collaborate seamlessly—delivering projects on time while boosting team productivity.",
      bullets: ["Visual Kanban boards for effortless organization", "Automated notifications to keep everyone aligned", "Advanced timelines to ensure nothing falls through the cracks"],
      color: "from-blue-500 to-indigo-600",
      iconColor: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: ClockIcon,
      title: "Precision Time Tracking & Automated Billing",
      description: "Never lose track of a billable minute again. Log time effortlessly, generate professional invoices automatically, and get paid faster with seamless integrations—transforming your team's hard work into reliable revenue growth.",
      bullets: ["Intelligent time capture with one-click logging", "Automated invoice creation from time entries", "Streamlined payments that save you hours every month"],
      color: "from-green-500 to-emerald-600",
      iconColor: "text-green-600 dark:text-green-400"
    },
    {
      icon: FileTextIcon,
      title: "Impress Clients with Branded Portals",
      description: "Elevate client relationships with secure, customizable portals. Share updates, approve documents, and track everything in real-time—building trust and loyalty while reducing back-and-forth communication.",
      bullets: ["Fully branded client experiences", "Secure document sharing and e-signatures", "Real-time progress updates that wow clients"],
      color: "from-purple-500 to-violet-600",
      iconColor: "text-purple-600 dark:text-purple-400"
    },
    {
      icon: Users2,
      title: "Seamless Team Collaboration",
      description: "Unite your team with integrated chat, shared calendars, and role-based access. Foster creativity and efficiency, whether remote or in-office, and watch productivity soar with tools designed for modern teams.",
      bullets: ["Real-time messaging with @mentions and channels", "Shared calendars for effortless scheduling", "Granular permissions to keep everything secure"],
      color: "from-orange-500 to-red-600",
      iconColor: "text-orange-600 dark:text-orange-400"
    },
    {
      icon: MessageCircle,
      title: "Robust Support & Knowledge Resources",
      description: "Empower everyone with intelligent ticketing and a searchable knowledge base. Resolve issues faster, onboard new members effortlessly, and scale support without the hassle of scattered tools.",
      bullets: ["Smart ticketing with priority routing", "Built-in knowledge base for self-service", "Centralized resource library for instant access"],
      color: "from-pink-500 to-rose-600",
      iconColor: "text-pink-600 dark:text-pink-400"
    },
    {
      icon: BarChart3,
      title: "Actionable Analytics & Insights",
      description: "Gain crystal-clear visibility into your business with intuitive dashboards. Track revenue, monitor time usage, and uncover growth opportunities—making data-driven decisions that propel your success.",
      bullets: ["Real-time performance metrics", "Customizable reports and forecasts", "Insights that turn data into dollars"],
      color: "from-cyan-500 to-teal-600",
      iconColor: "text-cyan-600 dark:text-cyan-400"
    }
  ];

  // Updated: Why Choose Us benefits section without security specifics or duplicates
  const whyChooseUs = [
    {
      icon: Palette,
      title: "Easy Customization",
      description: "Tailor the platform to match your brand with custom colors, logos, and layouts. Create a personalized experience that reflects your company's identity and makes every interaction feel seamless and professional.",
      color: "from-purple-500 to-violet-600"
    },
    {
      icon: Zap,
      title: "Time-Saving Automation",
      description: "Automate repetitive tasks like invoice generation from time logs, client notifications, and workflow approvals. Eliminate manual data entry and reduce administrative overhead, allowing your team to focus on high-value work.",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: TrendingUp,
      title: "Reliable Performance",
      description: "Built on a robust infrastructure with 99.9% uptime guarantees, our platform delivers fast, responsive experiences even under heavy use. Real-time updates and seamless integrations ensure smooth operations without interruptions.",
      color: "from-green-500 to-emerald-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/2 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl animate-bounce"></div>
      </div>

      {/* Enhanced Hero: More compelling value prop */}
      <section className="relative py-20 overflow-hidden">
        <div className="container max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="flex flex-col items-center space-y-6 mb-16">
            <img src="/WWConnectlogo.png" alt="WightWay Connect Logo" className="h-56 w-56 animate-float" />
            <h1 className="text-5xl md:text-7xl font-bold text-foreground animate-fade-in-up">
              Secure Client Portal & Team Operations Platform
            </h1>
            <p className="text-xl md:text-2xl text-foreground/90 max-w-4xl mx-auto leading-relaxed animate-fade-in-up delay-100">
              Manage projects, track time, invoice clients, and collaborate—all in one secure, scalable solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 animate-fade-in-up delay-200">
              <Button size="lg" asChild className="w-full sm:w-auto shadow-lg hover:shadow-xl transition-all bg-primary text-primary-foreground">
                <Link to="/signup">
                  <Shield className="mr-2 h-5 w-5" />
                  Start Now (7-day money back guarantee)
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto">
                <Link to="/login">
                  Access Your Dashboard
                </Link>
              </Button>
            </div>
          </div>
          {/* Updated Quick Stats: Generic benefits without numbers */}
          <div className="grid md:grid-cols-3 gap-8 animate-fade-in-up delay-300">
            <Card className="bg-white/80 backdrop-blur-sm border-primary/20 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">Trusted Platform</CardTitle>
                <Building className="h-4 w-4 text-primary animate-pulse" />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-foreground/90">Join innovative businesses worldwide scaling with confidence.</CardDescription>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-secondary/20 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">Time-Saving Automation</CardTitle>
                <TrendingUp className="h-4 w-4 text-secondary animate-pulse" />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-foreground/90">Automate admin tasks and focus on what drives your success.</CardDescription>
              </CardContent>
            </Card>
            <Card className="bg-white/80 backdrop-blur-sm border-accent/20 hover:shadow-lg transition-all duration-300 transform hover:scale-105">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-foreground">Reliable Performance</CardTitle>
                <CheckCircle className="h-4 w-4 text-accent animate-pulse" />
              </CardHeader>
              <CardContent>
                <CardDescription className="text-foreground/90">Reliable performance you can count on, every single day.</CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-24 bg-gradient-to-b from-muted/20 to-background">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-20 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Everything You Need in One Platform
            </h2>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
              WightWay Connect combines powerful tools into a single, intuitive experience. Say goodbye to fragmented apps and hello to streamlined success.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-0 bg-white/80 backdrop-blur-sm hover:from-primary/5 animate-fade-in-up delay-100">
                  <CardHeader className="pb-4">
                    <div className={`p-4 bg-gradient-to-r ${feature.color} rounded-2xl w-fit mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className={`h-8 w-8 text-white drop-shadow-sm ${feature.iconColor}`} />
                    </div>
                    <CardTitle className={`text-xl font-bold text-foreground group-hover:text-primary transition-all`}>{feature.title}</CardTitle>
                    <CardDescription className="text-foreground/90 leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {feature.bullets.map((bullet, i) => (
                        <li key={i} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-muted/50">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center mb-20 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Why Choose WightWay Connect?
            </h2>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto leading-relaxed">
              Built for businesses that demand excellence. Here's what sets us apart.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {whyChooseUs.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-0 bg-white/80 backdrop-blur-sm hover:from-primary/5 animate-fade-in-up delay-100">
                  <CardHeader className="pb-4">
                    <div className={`p-4 bg-gradient-to-r ${benefit.color} rounded-2xl w-fit mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Icon className={`h-8 w-8 text-white drop-shadow-sm`} />
                    </div>
                    <CardTitle className={`text-xl font-bold text-foreground group-hover:text-primary transition-all`}>{benefit.title}</CardTitle>
                    <CardDescription className="text-foreground/90 leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary/10 to-muted">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-foreground/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Start with our 7-day money back guarantee—no risk, all reward.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button size="lg" asChild className="bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all">
              <Link to="/signup">
                <Shield className="mr-2 h-5 w-5" />
                Start Now (7-day money back guarantee)
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/pricing">
                View Pricing
              </Link>
            </Button>
          </div>
          <p className="text-sm text-foreground/70 mt-8">
            7-day money back guarantee • GDPR compliant • UK-based support
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;